import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Calendar } from 'assets/icons/Calendar.svg';
import { ReactComponent as LayoutTwo } from 'assets/icons/LayoutTwo.svg';
import { ReactComponent as Messages } from 'assets/icons/Messages.svg';
import { ReactComponent as Video } from 'assets/icons/Video.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useDefaultLandingPagePreference from 'modules/Authentication/hooks/useDefaultLandingPagePreference';
import { LandingSection } from 'modules/Authentication/utils/setDefaultLandingPage';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import logger from 'utils/logging/faro';
import { isGuestModeEnabled } from 'utils/utils';

import LayoutCard from './LayoutCard';
import { CalendarPreview, MessengerPreview, VideoCallsPreview } from './LayoutTab.previews';

const LayoutTab = () => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const { matrixClient } = useMatrixContext();
  const { preference, setPreference } = useDefaultLandingPagePreference();
  const [savingSection, setSavingSection] = useState<LandingSection | null>(null);
  const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(matrixClient.getUserId() ?? '');
  // Guests never see the messenger card, so the 'messenger' store default would
  // leave nothing selected. Map it onto 'calls' for guests (display-only).
  const effectivePreference: LandingSection = isGuestUser && preference === 'messenger' ? 'calls' : preference;

  const handleSelect = async (section: LandingSection) => {
    if (section === preference || savingSection) return;
    setSavingSection(section);
    try {
      await setPreference(section);
    } catch (error) {
      logger.error('Failed to save default landing page', error);
      pushToast({ variant: 'error', title: t('default_landing.save_error') });
    } finally {
      setSavingSection(null);
    }
  };

  const isSavingSection = (section: LandingSection) => savingSection === section;

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-gray-30 bg-white p-4">
      <div className="flex w-full items-start gap-2">
        <div className="rounded-lg bg-gray-20 p-1 text-primary-70">
          <LayoutTwo className="h-5 w-5 stroke-current stroke-2" />
        </div>
        <span className="text-base font-medium text-black">{t('layout.heading')}</span>
      </div>
      <div className="h-px w-full bg-gray-30" />
      <div
        className="flex w-full flex-col gap-2 md:gap-4 lg:flex-row"
        role="radiogroup"
        aria-label={t('layout.heading')}
      >
        <LayoutCard
          value="calls"
          selected={effectivePreference === 'calls'}
          saving={isSavingSection('calls')}
          onSelect={handleSelect}
          title={t('layout.video_calls')}
          description={t('layout.video_calls_description')}
          icon={Video}
          previewContent={<VideoCallsPreview />}
        />
        {!isGuestUser && (
          <LayoutCard
            value="messenger"
            selected={effectivePreference === 'messenger'}
            saving={isSavingSection('messenger')}
            onSelect={handleSelect}
            title={t('layout.messenger')}
            description={t('layout.messenger_description')}
            icon={Messages}
            previewContent={<MessengerPreview />}
          />
        )}
        <LayoutCard
          value="calendar"
          selected={effectivePreference === 'calendar'}
          saving={isSavingSection('calendar')}
          onSelect={handleSelect}
          title={t('layout.calendar')}
          description={t('layout.calendar_description')}
          icon={Calendar}
          previewContent={<CalendarPreview />}
        />
      </div>
    </div>
  );
};

export default LayoutTab;
